import type { I18n } from "@lingui/core";
import { DateTime } from "luxon";
import { formatDate } from "../../../formatters/formatDate";
import { formatDuration } from "../../../formatters/formatDuration/formatDuration";
import { formatList } from "../../../formatters/formatList";

// Restores the per-placeholder value formatting the deleted i18next `formattersPlugin`
// supplied (`{{- date, time}}`, `{{- max, duration}}`, …). Lingui's ICU messages carry no
// formatter annotations, so the message key selects which formatter each value gets. Output
// must stay byte-identical to the old plugin — the rule e2e specs assert exact strings.

// hh:mm:ss → "2:30 PM"
const formatTime = (value: unknown): unknown => {
	if (typeof value !== "string") {
		return value;
	}
	const [hours, minutes, seconds] = value.split(":");
	const date = new Date();
	date.setHours(Number(hours), Number(minutes), Number(seconds ?? 0));
	return new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" }).format(date);
};

// yyyy-MM-dd → "Feb 1, 2030"
const formatDateValue = (value: unknown): unknown => {
	if (value instanceof Date) {
		return formatDate({ date: value, formatType: "Abbreviated_Date", language: "en" });
	}
	if (typeof value !== "string") {
		return value;
	}
	const date = DateTime.fromFormat(value, "yyyy-MM-dd", { zone: "utc" });
	return date.isValid
		? formatDate({ date: date.toJSDate(), formatType: "Abbreviated_Date", timezone: "UTC", language: "en" })
		: value;
};

// ISO datetime / Date → "Feb 1, 2030, 1:30 PM"
const formatDateTime = (value: unknown): unknown => {
	if (typeof value !== "string" && typeof value !== "number" && !(value instanceof Date)) {
		return value;
	}
	return new Intl.DateTimeFormat("en", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	}).format(new Date(value));
};

const formatOrList = (value: unknown, locale: string): unknown => {
	if (!Array.isArray(value)) {
		return value;
	}
	return formatList({ values: value.map((item) => String(item)), locale, type: "disjunction" });
};

const formatDurationValue = (value: unknown, i18n: I18n): unknown =>
	typeof value === "number" || (typeof value === "object" && value !== null)
		? formatDuration({
				duration: value as Parameters<typeof formatDuration>[0]["duration"],
				i18n,
			})
		: value;

// Maps a custom-issue message key to the value formatters its placeholders used in the old
// i18next templates, then formats the matching keys in `values`.
export const formatCustomIssueValues = ({
	i18n,
	key,
	values,
}: {
	i18n: I18n;
	key: string;
	values: Record<string, unknown>;
}): Record<string, unknown> => {
	const formatValue = (valueKey: string, format: (value: unknown) => unknown) =>
		valueKey in values ? { [valueKey]: format(values[valueKey]) } : undefined;

	const overrides =
		key.startsWith("errors.before_time.") || key.startsWith("errors.after_time.")
			? formatValue("date", formatTime)
			: key.startsWith("errors.before_date.") || key.startsWith("errors.after_date.")
				? formatValue("date", formatDateValue)
				: key.startsWith("errors.before_datetime.") || key.startsWith("errors.after_datetime.")
					? formatValue("datetime", formatDateTime)
					: key.startsWith("errors.duration_max.") || key.startsWith("errors.duration_min.")
						? {
								...formatValue("max", (v) => formatDurationValue(v, i18n)),
								...formatValue("min", (v) => formatDurationValue(v, i18n)),
							}
						: key === "errors.invalid_day_of_week"
							? formatValue("allowedDays", (v) => formatOrList(v, i18n.locale))
							: undefined;

	return overrides ? { ...values, ...overrides } : values;
};

// yyyy-MM-dd / Date (zod date-origin bound) → "Feb 1, 2030, 1:30 PM"
export const formatDateBound = (value: unknown): unknown => formatDateTime(value);
